<script setup lang="ts">
  import type { ResolvableLink } from '@unhead/vue'

  import { DialogProvider } from '@uplatform-ai/fe-utils'
  import { dateEnUS, dateRuRU, enUS, ruRU } from 'naive-ui'

  import { LayoutProvider } from '@/layouts'

  import { themeNaiveUi } from '@/config/themeNaiveUi'

  import {
    DEFAULT_NOTIFICATION_PLACEMENT,
    isHolidaySeason,
    isValentineSeason,
    NOTIFICATION_PLACEMENT_KEY,
    NOTIFICATIONS_LAYER_ID,
    promoteNotificationsLayer,
    useScreenSize,
  } from '@/shared/lib'
  import type { NotificationPlacement } from '@/shared/lib'
  import { useTenant } from '@/shared/lib/tenant'

  import { useUplatformLocale } from '@/features/locale'

  import { TheHearts, TheSnowflakes } from '@/widgets/layout'
  import { UpdatePagePrompt } from '@/widgets/updatePagePrompt'

  const { currentLocale, updateLocale } = useUplatformLocale()

  updateLocale()

  const { isTablet } = storeToRefs(useScreenSize())
  const route = useRoute()

  const { tenantId } = useTenant()
  const base = computed(() => `/tenants/${tenantId}`)

  useHead({
    link: [
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: () => `${base.value}/favicon-32x32.png` },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: () => `${base.value}/favicon-16x16.png` },
      { rel: 'icon', type: 'image/png', sizes: '192x192', href: () => `${base.value}/android-chrome-192x192.png` },
      { rel: 'icon', type: 'image/png', sizes: '512x512', href: () => `${base.value}/android-chrome-512x512.png` },
      { rel: 'apple-touch-icon', href: () => `${base.value}/apple-touch-icon.png` },
      { rel: 'shortcut icon', href: () => `${base.value}/favicon.ico` },
      { rel: 'msapplication-TileImage', href: () => `${base.value}/mstile-150x150.png` },
      { rel: 'manifest', href: () => `${base.value}/site.webmanifest` },
      { rel: 'msapplication-config', href: () => `${base.value}/browserconfig.xml` },
    ] as ResolvableLink[],
  })

  const showSnowflakes = computed(() => {
    if (isTablet.value) {
      return false
    }
    return isHolidaySeason()
  })

  const showHearts = computed(() => {
    if (isTablet.value) {
      return false
    }
    return isValentineSeason()
  })

  const locales = {
    en: { locale: enUS, dateLocale: dateEnUS },
    ru: { locale: ruRU, dateLocale: dateRuRU },
  } as const
  const localeProps = computed(() => locales[currentLocale.value] ?? locales.en)

  const notificationPlacement = ref<NotificationPlacement>(DEFAULT_NOTIFICATION_PLACEMENT)
  provide(NOTIFICATION_PLACEMENT_KEY, notificationPlacement)

  const notificationsLayerTarget = `#${NOTIFICATIONS_LAYER_ID}`

  // Baseline: слой в top layer до первой нотификации
  onMounted(() => {
    promoteNotificationsLayer()
  })
</script>

<template>
  <NConfigProvider
    :theme-overrides="themeNaiveUi"
    v-bind="localeProps"
  >
    <NLoadingBarProvider>
      <NMessageProvider
        :placement="notificationPlacement"
        :to="notificationsLayerTarget"
      >
        <NNotificationProvider>
          <DialogProvider>
            <div class="bg-surface-bg relative">
              <TheSnowflakes v-if="showSnowflakes" />
              <TheHearts
                v-if="showHearts"
                :fullscreen="route.name === '/auth/sign-in'"
              />
              <UpdatePagePrompt />
              <LayoutProvider>
                <Suspense>
                  <RouterView />
                </Suspense>
              </LayoutProvider>
            </div>
          </DialogProvider>
        </NNotificationProvider>
      </NMessageProvider>
    </NLoadingBarProvider>
  </NConfigProvider>
  <!-- Слой top layer для сообщений поверх нативных dialog.showModal() -->
  <div
    :id="NOTIFICATIONS_LAYER_ID"
    popover="manual"
  />
</template>

<style lang="scss">
  @include disableQuasarFormatting;

  html {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    scrollbar-gutter: stable;
  }

  body {
    background-color: var(--background-bg-primary);
  }
</style>
